import type { EncryptedData } from '../auth/crypto';
import type { AccountRecord } from './accounts';

export const BACKUP_FORMAT = 'terminal-nsi-backup';

/** Contenu d'un fichier `.nsi-backup` : compte public + données chiffrées. */
export interface BackupData {
  format: typeof BACKUP_FORMAT;
  version: 1;
  account: AccountRecord;
  blob: EncryptedData;
}

export function createBackup(record: AccountRecord, blob: EncryptedData): string {
  const payload: BackupData = {
    format: BACKUP_FORMAT,
    version: 1,
    account: { ...record },
    blob,
  };
  return JSON.stringify(payload, null, 2);
}

function isEncryptedData(value: unknown): value is EncryptedData {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<EncryptedData>;
  return typeof candidate.iv === 'string' && typeof candidate.data === 'string';
}

export function parseBackup(text: string): BackupData | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const data = parsed as Partial<BackupData>;
  if (data.format !== BACKUP_FORMAT || data.version !== 1) return null;

  const account = data.account;
  if (
    !account ||
    typeof account.id !== 'string' ||
    typeof account.salt !== 'string' ||
    typeof account.iterations !== 'number' ||
    !isEncryptedData(account.verifier)
  ) {
    return null;
  }
  if (!isEncryptedData(data.blob)) return null;

  return {
    format: BACKUP_FORMAT,
    version: 1,
    account: account as AccountRecord,
    blob: data.blob,
  };
}
